import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, NgClass, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { ScrollRegionComponent } from '@brightside/angular-bds';
import { catchError, EMPTY, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { AuthService } from '@brightside/core/auth';
import {
  ActionModalService,
  SystemCodeCatalog,
  SystemCodeCatalogService,
  SystemCodeCatalogValue,
} from '@brightnav/care-plan';
import { HttpActionApiService } from '../http/http-action-api.service';
import {
  ActionResource,
  DueDateFilter,
  ListOpenActionsRequest,
} from '../http/action-api.model';
import {
  DueDateBucket,
  actionMatchesDueFilter,
  dueBucket,
} from '../due-date-filter';
import {
  TimelineBar,
  buildTimelineBars,
  timelineMaxTotal,
} from '../open-actions-timeline';
import { OpenActionsAnalytics } from '../analytics/open-actions-analytics';

const PAGE_SIZE = 20;
const ACTION_TYPE_CODE_TYPE = 'action_type';
const ACTION_PRIORITY_CODE_TYPE = 'action_priority';
const ACTION_STATUS_CODE_TYPE = 'action_status';
const DAY_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Header + eight body rows; the table viewport caps here then scrolls. */
export const OPEN_ACTIONS_TABLE_MAX_HEIGHT = '23rem';

/** List vs stacked due-by-date chart. Home opens on Timeline. */
export type OpenActionsViewMode = 'list' | 'timeline';

export const DUE_DATE_OPTIONS: { label: string; value: DueDateFilter }[] = [
  { label: 'All due dates', value: 'all' },
  { label: 'Overdue', value: 'overdue' },
  { label: 'Due today', value: 'today' },
  { label: 'This week', value: 'week' },
  { label: 'Next week', value: 'nextWeek' },
  { label: 'Later', value: 'later' },
];

/** Timeline chips; counts always use the full loaded set (no due-date AIM filter). */
export const DUE_DATE_CHIPS: { label: string; value: DueDateBucket }[] = [
  { label: 'Overdue', value: 'overdue' },
  { label: 'Due today', value: 'today' },
  { label: 'This week', value: 'week' },
  { label: 'Next week', value: 'nextWeek' },
  { label: 'Later', value: 'later' },
];

/** Sortable Open Actions columns. Default is due date, earliest first. */
export type OpenActionsSortColumn =
  | 'dueDate'
  | 'client'
  | 'customer'
  | 'actionType'
  | 'details'
  | 'status'
  | 'priority'
  | 'promise'
  | 'createdAt';

/**
 * Home “My Open Actions”: Timeline (default) stacked due-by-date chart and
 * List table. AIM is loaded without due-date bounds so chip counts stay on
 * the full search/type/priority set; due filters apply client-side. Bar click
 * opens List for that column; chip click stays on Timeline.
 */
@Component({
  selector: 'bn-open-actions-widget',
  standalone: true,
  imports: [
    DatePipe,
    NgClass,
    FormsModule,
    Button,
    InputText,
    Select,
    ScrollRegionComponent,
  ],
  templateUrl: './open-actions-widget.component.html',
  styleUrl: './open-actions-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpenActionsWidgetComponent {
  private readonly _api = inject(HttpActionApiService);
  private readonly _auth = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _codes = inject(SystemCodeCatalogService);
  private readonly _actionModal = inject(ActionModalService);
  private readonly _analytics = inject(OpenActionsAnalytics);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _titleCase = new TitleCasePipe();
  private readonly _loads = new Subject<number>();

  readonly search = signal('');
  readonly viewMode = signal<OpenActionsViewMode>('timeline');
  readonly dueDateFilter = signal<DueDateFilter>('all');
  readonly dayKey = signal<string | null>(null);
  readonly actionTypeCode = signal('');
  readonly priorityCode = signal('');
  readonly sortField = signal<OpenActionsSortColumn>('dueDate');
  readonly sortOrder = signal<1 | -1>(1);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly actions = signal<ActionResource[]>([]);
  readonly totalItems = signal(0);
  readonly offset = signal(0);
  readonly limit = PAGE_SIZE;
  readonly tableMaxHeight = OPEN_ACTIONS_TABLE_MAX_HEIGHT;
  readonly actionTypes = signal<SystemCodeCatalogValue[]>([]);
  readonly priorities = signal<SystemCodeCatalogValue[]>([]);
  readonly statuses = signal<SystemCodeCatalogValue[]>([]);
  readonly dueDateChips = DUE_DATE_CHIPS;
  readonly uniqueClientCount = computed(() => {
    const ids = new Set(
      this.actions()
        .map((action) => action.client.id)
        .filter((id) => Number.isFinite(id)),
    );
    return ids.size;
  });
  readonly summary = computed(() => {
    const total = this.totalItems();
    const unique = this.uniqueClientCount();
    const actionLabel = `${total} open ${total === 1 ? 'action' : 'actions'}`;
    if (!this.actions().length) {
      return actionLabel;
    }
    return `${actionLabel} · ${unique} ${unique === 1 ? 'client' : 'clients'}`;
  });
  /**
   * AIM rows matching the current due-date chip, List dropdown, or Timeline
   * bar drill. Search/type/priority already applied by the AIM load.
   */
  readonly dueFilteredActions = computed(() =>
    this._matchingDue(this.actions()),
  );
  readonly pageLabel = computed(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(this.dueFilteredActions().length / this.limit) || 1,
    );
    const current = Math.floor(this.offset() / this.limit) + 1;
    return `Page ${current} of ${totalPages}`;
  });
  readonly actionTypeOptions = computed(() => [
    { label: 'All action types', value: '' },
    ...this.actionTypes().map((item) => ({
      label: item.label,
      value: item.code,
    })),
  ]);
  readonly priorityOptions = computed(() => [
    { label: 'All priorities', value: '' },
    ...this.priorities().map((item) => ({
      label: item.label,
      value: item.code,
    })),
  ]);
  readonly dueDateOptions = computed(() => {
    const filter = this.dueDateFilter();
    const dayKey = this.dayKey();
    if (filter === 'day' && dayKey) {
      return [
        ...DUE_DATE_OPTIONS,
        { label: this._formatDayKey(dayKey), value: 'day' as const },
      ];
    }
    if (filter === 'beyond') {
      return [
        ...DUE_DATE_OPTIONS,
        { label: '3+ weeks', value: 'beyond' as const },
      ];
    }
    return DUE_DATE_OPTIONS;
  });
  readonly chipCounts = computed(() => {
    const now = new Date();
    const counts: Record<DueDateBucket, number> = {
      overdue: 0,
      today: 0,
      week: 0,
      nextWeek: 0,
      later: 0,
    };
    for (const action of this.actions()) {
      const bucket = dueBucket(action.dueDate, now);
      if (bucket) {
        counts[bucket] += 1;
      }
    }
    return counts;
  });
  readonly timelineBars = computed(() =>
    buildTimelineBars(this.dueFilteredActions(), new Date()),
  );
  readonly timelineMax = computed(() => timelineMaxTotal(this.timelineBars()));
  readonly filtersDefault = computed(
    () =>
      !this.search().trim() &&
      this.dueDateFilter() === 'all' &&
      !this.dayKey() &&
      !this.actionTypeCode() &&
      !this.priorityCode(),
  );
  /**
   * Due-filtered AIM result, sorted then sliced to the current page.
   * AIM has no sort query, so Home loads every matching page before sorting.
   */
  readonly displayedActions = computed(() => {
    const field = this.sortField();
    const dir = this.sortOrder();
    const sorted = [...this.dueFilteredActions()].sort((a, b) => {
      const empty = this._emptyRank(a, b, field);
      if (empty !== 0) {
        return empty;
      }
      return this._compare(a, b, field) * dir;
    });
    return sorted.slice(this.offset(), this.offset() + this.limit);
  });

  constructor() {
    this._codes
      .load()
      .pipe(
        catchError(() => of({} as SystemCodeCatalog)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((catalog) => {
        this.actionTypes.set(this._group(catalog, ACTION_TYPE_CODE_TYPE));
        this.priorities.set(this._group(catalog, ACTION_PRIORITY_CODE_TYPE));
        this.statuses.set(this._group(catalog, ACTION_STATUS_CODE_TYPE));
      });
    this._loads
      .pipe(
        switchMap((nextOffset) => this._load$(nextOffset)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
    this._analytics.viewSelected(this.viewMode());
    this.reload();
  }

  reload(nextOffset = 0): void {
    this._loads.next(nextOffset);
  }

  private _load$(nextOffset: number) {
    const assignedToId = this._auth.getOktaUid();
    if (!assignedToId) {
      this.error.set('Could not read your Okta user id. Try signing in again.');
      this.actions.set([]);
      this.loading.set(false);
      return EMPTY;
    }

    this.loading.set(true);
    this.error.set(null);
    const request = {
      assignedToId,
      search: this.search().trim() || undefined,
      actionTypeCodes: this.actionTypeCode() || undefined,
      priorityCodes: this.priorityCode() || undefined,
      limit: this.limit,
    };
    return this._fetchAll$(request).pipe(
      tap((all) => {
        this.actions.set(all);
        this.totalItems.set(all.length);
        this.offset.set(
          Math.min(nextOffset, this._lastOffset(this._matchingDue(all).length)),
        );
        this.loading.set(false);
      }),
      catchError(() => {
        this.error.set("We couldn't load your open actions right now.");
        this.loading.set(false);
        return EMPTY;
      }),
    );
  }

  /**
   * Walks AIM pages until the filtered collection is complete so sort can
   * run on the full set before client-side pagination.
   */
  private _fetchAll$(
    request: ListOpenActionsRequest,
    offset = 0,
    acc: ActionResource[] = [],
  ): Observable<ActionResource[]> {
    return this._api.listOpenActions({ ...request, offset }).pipe(
      switchMap((payload): Observable<ActionResource[]> => {
        const merged = acc.concat(payload.actions);
        const next = offset + payload.actions.length;
        if (
          payload.actions.length === 0 ||
          next >= payload.pagination.totalItems ||
          next <= offset
        ) {
          return of(merged);
        }
        return this._fetchAll$(request, next, merged);
      }),
    );
  }

  private _lastOffset(totalItems: number): number {
    return totalItems <= 0
      ? 0
      : Math.floor((totalItems - 1) / this.limit) * this.limit;
  }

  onSearch(): void {
    this.reload(0);
  }

  /**
   * Switches List/Timeline. Bar drills (`day` / `beyond`) are not chips, so
   * returning to Timeline clears them; chip buckets round-trip.
   *
   * @param mode - Target view.
   */
  setViewMode(mode: OpenActionsViewMode): void {
    if (this.viewMode() === mode) {
      return;
    }
    this.viewMode.set(mode);
    this._analytics.viewSelected(mode);
    const filter = this.dueDateFilter();
    if (mode === 'timeline' && (filter === 'day' || filter === 'beyond')) {
      this.dueDateFilter.set('all');
      this.dayKey.set(null);
    }
    this.offset.set(0);
  }

  /**
   * Timeline chip: stay on Timeline, filter bars, or clear when clicked again.
   *
   * @param bucket - Exclusive due-date chip.
   */
  onChipClick(bucket: DueDateBucket): void {
    this.dayKey.set(null);
    if (this.dueDateFilter() === bucket) {
      this.dueDateFilter.set('all');
    } else {
      this.dueDateFilter.set(bucket);
    }
    this.offset.set(0);
  }

  /**
   * Clears the Timeline chip / bar due-date slice without reloading AIM.
   */
  showAllDueDates(): void {
    this.dueDateFilter.set('all');
    this.dayKey.set(null);
    this.offset.set(0);
  }

  /**
   * Tone + selected/empty modifiers for a Timeline due-date chip.
   *
   * @param bucket - Chip bucket.
   * @returns Host classes for the chip button.
   */
  filterChipClass(bucket: DueDateBucket): string {
    const empty = this.chipCounts()[bucket] === 0 ? ' bn-open-actions__filter-chip--empty' : '';
    const active =
      this.dueDateFilter() === bucket ? ' bn-open-actions__filter-chip--active' : '';
    return `bn-open-actions__filter-chip--${bucket}${empty}${active}`;
  }

  /**
   * Opens List for the clicked column (overdue, one calendar day, or 3+ wks).
   *
   * @param bar - Timeline column.
   */
  onBarClick(bar: TimelineBar): void {
    this.setViewMode('list');
    this.offset.set(0);
    if (bar.kind === 'overdue') {
      this.dueDateFilter.set('overdue');
      this.dayKey.set(null);
      return;
    }
    if (bar.kind === 'beyond') {
      this.dueDateFilter.set('beyond');
      this.dayKey.set(null);
      return;
    }
    this.dueDateFilter.set('day');
    this.dayKey.set(bar.dayKey ?? null);
  }

  /**
   * List due-date dropdown. Does not reload AIM; filtering is client-side.
   *
   * @param value - Dropdown due-date filter.
   */
  onDueDateChange(value: DueDateFilter): void {
    this.dueDateFilter.set(value);
    if (value !== 'day') {
      this.dayKey.set(null);
    }
    this.offset.set(0);
  }

  onActionTypeChange(value: string): void {
    this.actionTypeCode.set(value);
    this.reload(0);
  }

  onPriorityChange(value: string): void {
    this.priorityCode.set(value);
    this.reload(0);
  }

  /**
   * Clears search and dropdown filters, then reloads the first page.
   * Column sort and the current List/Timeline mode are left as the FA last chose.
   */
  resetFilters(): void {
    this.search.set('');
    this.dueDateFilter.set('all');
    this.dayKey.set(null);
    this.actionTypeCode.set('');
    this.priorityCode.set('');
    this.reload(0);
  }

  /**
   * Toggles the active column, matching Accounts (`p-table` single sort).
   *
   * @param field - Column to sort.
   */
  onSort(field: OpenActionsSortColumn): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() === 1 ? -1 : 1);
    } else {
      this.sortField.set(field);
      this.sortOrder.set(1);
    }
    this.offset.set(0);
  }

  ariaSort(field: OpenActionsSortColumn): 'ascending' | 'descending' | 'none' {
    if (this.sortField() !== field) {
      return 'none';
    }
    return this.sortOrder() === 1 ? 'ascending' : 'descending';
  }

  sortIconClass(field: OpenActionsSortColumn): string {
    if (this.sortField() !== field) {
      return 'pi-sort-alt';
    }
    return this.sortOrder() === 1
      ? 'pi-sort-amount-up-alt'
      : 'pi-sort-amount-down-alt';
  }

  nextPage(): void {
    this.offset.set(this.offset() + this.limit);
  }

  previousPage(): void {
    this.offset.set(Math.max(0, this.offset() - this.limit));
  }

  canPrevious(): boolean {
    return this.offset() > 0;
  }

  canNext(): boolean {
    return this.offset() + this.limit < this.dueFilteredActions().length;
  }

  /**
   * Stack height as a percent of the Timeline y-scale.
   *
   * @param bar - Column whose total to scale.
   * @returns Height percentage in `0…100`.
   */
  stackPercent(bar: TimelineBar): number {
    if (bar.total <= 0) {
      return 0;
    }
    return (bar.total / this.timelineMax()) * 100;
  }

  /**
   * Accessible name for a Timeline column control.
   *
   * @param bar - Column to describe.
   * @returns Button label including counts and the List drill affordance.
   */
  barAriaLabel(bar: TimelineBar): string {
    const count = `${bar.total} ${bar.total === 1 ? 'action' : 'actions'}`;
    const stack =
      bar.high > 0 ? `${count}, ${bar.high} high priority` : count;
    if (bar.kind === 'overdue') {
      return `Overdue, ${stack}. Show list`;
    }
    if (bar.kind === 'beyond') {
      return `Due in 3 or more weeks, ${stack}. Show list`;
    }
    return `${bar.label}, ${bar.sublabel}, ${stack}. Show list`;
  }

  openClient(event: Event, action: ActionResource): void {
    event.stopPropagation();
    const guid = action.client.guid?.trim();
    if (!guid) {
      return;
    }
    void this._router.navigate(['/brightnav/myclients'], {
      queryParams: { guid },
    });
  }

  hasClientGuid(action: ActionResource): boolean {
    return !!action.client.guid?.trim();
  }

  /**
   * Title-cases AIM customer names for the table cell.
   *
   * @param customer - Raw AIM customer string.
   * @returns Title Case text, or an em dash when empty.
   */
  formatCustomer(customer: string | null | undefined): string {
    const raw = customer?.trim();
    if (!raw) {
      return '—';
    }
    return this._titleCase.transform(raw) ?? raw;
  }

  async openAction(action: ActionResource): Promise<void> {
    const updated = await this._actionModal.open(action.actionId, {
      entryPoint: 'home_open_actions',
    });
    if (updated) {
      this.reload(this.offset());
    }
  }

  openActionFromKeyboard(event: Event, action: ActionResource): void {
    if (event.target !== event.currentTarget) {
      return;
    }
    event.preventDefault();
    void this.openAction(action);
  }

  typeLabel(code: string | null): string {
    return this._catalogLabel(this.actionTypes(), code);
  }

  statusLabel(code: string | null): string {
    return this._catalogLabel(this.statuses(), code);
  }

  priorityLabel(code: string | null): string {
    return this._catalogLabel(this.priorities(), code);
  }

  dueTone(action: ActionResource): 'overdue' | 'soon' | 'later' | 'none' {
    if (!action.dueDate) {
      return 'none';
    }
    const due = new Date(action.dueDate);
    if (Number.isNaN(due.getTime())) {
      return 'none';
    }
    const startToday = new Date();
    startToday.setHours(0, 0, 0, 0);
    const dueDay = new Date(due);
    dueDay.setHours(0, 0, 0, 0);
    const diffDays = Math.round(
      (dueDay.getTime() - startToday.getTime()) / 86_400_000,
    );
    if (diffDays < 0) {
      return 'overdue';
    }
    if (diffDays <= 3) {
      return 'soon';
    }
    return 'later';
  }

  private _matchingDue(actions: ActionResource[]): ActionResource[] {
    const filter = this.dueDateFilter();
    const dayKey = this.dayKey();
    const now = new Date();
    return actions.filter((action) =>
      actionMatchesDueFilter(action.dueDate, filter, now, dayKey),
    );
  }

  private _formatDayKey(dayKey: string): string {
    const [year, month, day] = dayKey.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) {
      return dayKey;
    }
    return `${DAY_MONTHS[date.getMonth()]} ${date.getDate()}`;
  }

  private _group(
    catalog: SystemCodeCatalog,
    codeType: string,
  ): SystemCodeCatalogValue[] {
    try {
      return [...this._codes.values(catalog, codeType)];
    } catch {
      return [];
    }
  }

  private _catalogLabel(
    values: readonly SystemCodeCatalogValue[],
    code: string | null,
  ): string {
    if (!code) {
      return '—';
    }
    return values.find((item) => item.code === code)?.label ?? code;
  }

  private _emptyRank(
    a: ActionResource,
    b: ActionResource,
    field: OpenActionsSortColumn,
  ): number {
    const aEmpty = this._isEmpty(a, field);
    const bEmpty = this._isEmpty(b, field);
    if (aEmpty === bEmpty) {
      return 0;
    }
    return aEmpty ? 1 : -1;
  }

  private _isEmpty(
    action: ActionResource,
    field: OpenActionsSortColumn,
  ): boolean {
    switch (field) {
      case 'dueDate':
        return !this._time(action.dueDate);
      case 'createdAt':
        return !this._time(action.createdAt);
      case 'client':
        return !action.client.name?.trim();
      case 'customer':
        return !action.client.customer?.trim();
      case 'actionType':
        return this.typeLabel(action.actionTypeCode) === '—';
      case 'details':
        return !action.name?.trim();
      case 'status':
        return this.statusLabel(action.statusCode) === '—';
      case 'priority':
        return this.priorityLabel(action.priorityCode) === '—';
      case 'promise':
        return false;
    }
  }

  private _compare(
    a: ActionResource,
    b: ActionResource,
    field: OpenActionsSortColumn,
  ): number {
    switch (field) {
      case 'dueDate':
        return this._time(a.dueDate) - this._time(b.dueDate);
      case 'createdAt':
        return this._time(a.createdAt) - this._time(b.createdAt);
      case 'client':
        return this._text(a.client.name, b.client.name);
      case 'customer':
        return this._text(a.client.customer, b.client.customer);
      case 'actionType':
        return this._text(
          this.typeLabel(a.actionTypeCode),
          this.typeLabel(b.actionTypeCode),
        );
      case 'details':
        return this._text(a.name, b.name);
      case 'status':
        return this._text(
          this.statusLabel(a.statusCode),
          this.statusLabel(b.statusCode),
        );
      case 'priority':
        return this._text(
          this.priorityLabel(a.priorityCode),
          this.priorityLabel(b.priorityCode),
        );
      case 'promise':
        return Number(a.clientPromise) - Number(b.clientPromise);
    }
  }

  private _time(value: string | Date | null | undefined): number {
    if (!value) {
      return 0;
    }
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  private _text(a: string | null | undefined, b: string | null | undefined): number {
    return (a ?? '').localeCompare(b ?? '', 'en-US', { sensitivity: 'base' });
  }
}
